import { useEffect, useState } from 'react'
import { Search, Sparkles, BookOpen, MessageCircle, ArrowRight } from 'lucide-react'

const placeholders = [
  'Find Private Car calculator',
  'Why does my NCB show 25%?',
  "What does 'Bundled policy' mean?",
  'How is IDV calculated for a 4-year-old Swift?',
  'Why did my Nil-Deductible add-on disappear?',
]

const mockResults = [
  {
    type: 'Screen',
    label: 'Private Car Calculator',
    hint: 'Motor · 13 calculators',
    tint: 'bg-brand/10 text-brand',
  },
  {
    type: 'Knowledge',
    label: 'NCB tiers — 20 / 25 / 35 / 45 / 50 %',
    hint: 'Knowledge Base · 2 min read',
    tint: 'bg-purple-50 text-accent-purple',
  },
  {
    type: 'Troubleshoot',
    label: "Add-on greyed out? Check policy type.",
    hint: 'Help · common issues',
    tint: 'bg-amber-50 text-status-warningDark',
  },
]

export function FuzzySearch() {
  const [idx, setIdx] = useState(0)
  const [shown, setShown] = useState('')

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setShown(placeholders[0])
      return
    }
    let cancelled = false
    let i = 0
    const text = placeholders[idx]
    const tick = () => {
      if (cancelled) return
      if (i <= text.length) {
        setShown(text.slice(0, i))
        i += 1
        setTimeout(tick, 45)
      } else {
        setTimeout(() => {
          if (!cancelled) setIdx((v) => (v + 1) % placeholders.length)
        }, 1800)
      }
    }
    tick()
    return () => {
      cancelled = true
    }
  }, [idx])

  return (
    <section
      id="fuzzy"
      className="relative overflow-hidden"
      style={{
        background:
          'linear-gradient(180deg, rgba(88,86,214,0.06) 0%, rgba(247,248,250,0) 100%)',
      }}
    >
      <div className="mx-auto max-w-6xl px-5 py-16 md:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <span className="chip" style={{ background: '#FFF7ED', color: '#B45309' }}>
            <Sparkles size={12} /> Coming soon · beta
          </span>
          <h2 className="mt-4">Ask BimaUncle anything.</h2>
          <p className="mt-3 text-ink-soft">
            A single search bar inside the app finds anything — a calculator, a
            customer, a past quote, a setting. Type a question instead, and our
            Knowledge Base answers in plain English.
          </p>
        </div>

        <div className="mx-auto mt-10 max-w-2xl">
          <div
            className="rounded-card border border-white/60 p-5 shadow-card glass-overlay"
            style={{ boxShadow: '0 12px 40px -10px rgba(88,86,214,0.18)' }}
          >
            <div className="flex items-center gap-3 rounded-input border border-[var(--color-divider)] bg-white px-4 py-3">
              <Search size={20} className="text-accent-purple" />
              <div className="flex-1 truncate font-mono text-sm text-ink">
                {shown}
                <span className="ml-0.5 inline-block h-4 w-[2px] -translate-y-px animate-pulse bg-accent-purple" />
              </div>
              <kbd className="hidden rounded-md border border-[var(--color-divider)] bg-[var(--color-nav)] px-2 py-0.5 text-[10px] font-semibold text-ink-soft sm:inline">
                Cmd / Ctrl K
              </kbd>
            </div>

            <div className="mt-3 space-y-2">
              {mockResults.map((r) => (
                <div
                  key={r.label}
                  className="flex items-center gap-3 rounded-input border border-[var(--color-divider)] bg-white p-3"
                >
                  <span className={`chip ${r.tint}`}>{r.type}</span>
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{r.label}</div>
                    <div className="text-xs text-ink-soft">{r.hint}</div>
                  </div>
                  <ArrowRight size={16} className="text-ink-soft" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-3">
          <Tile
            icon={<Search size={20} />}
            title="Fuzzy across the whole app"
            body="Typos tolerated, partial matches, ranked by recency and relevance. Cmd / Ctrl-K everywhere."
          />
          <Tile
            icon={<BookOpen size={20} />}
            title="Product-aware Knowledge Base"
            body="Covers policy terms, conditions, rate logic, every add-on, and all known error messages."
          />
          <Tile
            icon={<MessageCircle size={20} />}
            title="Conversational answers"
            body="Ask in your own words. Get a cited, plain-English reply with deep links to the exact app screen."
          />
        </div>

        <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-ink-soft">
          Fuzzy Search + Knowledge Base is rolling out to Internal Testers
          first.{' '}
          <a href="#tester" className="font-semibold text-accent-purple hover:underline">
            Join below →
          </a>
        </p>
      </div>
    </section>
  )
}

function Tile({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode
  title: string
  body: string
}) {
  return (
    <div className="card">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-2xl"
        style={{ background: 'rgba(88,86,214,0.12)', color: '#5856D6' }}
      >
        {icon}
      </div>
      <h3 className="mt-4 text-lg">{title}</h3>
      <p className="mt-2 text-sm text-ink-soft">{body}</p>
    </div>
  )
}
